/**
 * DataList footer with pagination controls.
 *
 * Renders either traditional page-based pagination or a "Load more"
 * button depending on the configured pagination mode. Also displays
 * an item count summary and an optional page-size selector.
 */

import * as React from 'react';
import { Pagination } from '../../../base/navigation/Pagination/Pagination';
import { Button } from '../../../base/actions/Button/Button';
import { Select } from '../../../base/inputs/Select/Select';
import { Text } from '../../../base/display/Text/Text';
import { useDataListContext } from '../context/DataListContext';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../constants';
import type { DataListFooterProps } from '../types';

export function DataListFooter({ className }: Readonly<DataListFooterProps>) {
  const { config, list } = useDataListContext();
  const paginationConfig = config.pagination;
  const mode = paginationConfig?.mode ?? 'pagination';

  // Item count text
  const from = (list.page - 1) * list.pageSize + 1;
  const to = Math.min(list.page * list.pageSize, list.totalItems);
  const itemCountText =
    config.i18n?.itemsCount ?? `Showing ${from}-${to} of ${list.totalItems}`;

  // Page size options
  const pageSizeOptions = (
    paginationConfig?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS
  ).map((n) => ({ value: String(n), label: String(n) }));

  // Page size change handler — Select onChange passes string | string[]
  const handlePageSizeChange = React.useCallback(
    (value: string | string[]) => {
      /* v8 ignore start */
      const raw = Array.isArray(value) ? value[0] : value;
      /* v8 ignore stop */
      const parsed = Number(raw);
      /* v8 ignore start */
      if (!Number.isNaN(parsed)) {
        /* v8 ignore stop */
        list.setPageSize(parsed);
      }
    },
    [list],
  );

  const combinedClasses = [
    'flex items-center justify-between py-3',
    config.cssOverrides?.footer,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClasses} data-testid="data-list-footer">
      {/* Left side: Item count */}
      {paginationConfig?.showTotal !== false && list.totalItems > 0 && (
        <Text size="sm" color="muted">
          {itemCountText}
        </Text>
      )}

      {/* Right side: Pagination or Load More */}
      <div className="flex items-center gap-3 ml-auto">
        {/* Page size selector */}
        {mode === 'pagination' && paginationConfig?.showPageSize !== false && (
          <Select
            options={pageSizeOptions}
            value={String(list.pageSize)}
            onChange={handlePageSizeChange}
            size="sm"
            aria-label="Page size"
          />
        )}

        {/* Pagination mode */}
        {mode === 'pagination' && list.totalPages > 1 && (
          <Pagination
            page={list.page}
            totalPages={list.totalPages}
            onPageChange={list.setPage}
          />
        )}

        {/* Scroll / load-more mode */}
        {mode === 'scroll' && list.hasMore && (
          <Button
            variant="ghost"
            onClick={() => list.loadMore()}
            loading={list.isLoading}
          >
            Load more
          </Button>
        )}
      </div>
    </div>
  );
}

DataListFooter.displayName = 'DataListFooter';
